import { useState } from "react";
import { MapPin, CalendarDays, Clock, BadgeCheck, Flag, Users, Accessibility } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { CATEGORY_STYLES, STATUS_STYLES } from "@/lib/api";
import RegisterModal from "@/components/RegisterModal";
import ReportModal from "@/components/ReportModal";

export default function EventCard({ event, onRegistered }) {
  const [registerOpen, setRegisterOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const pct = Math.min(100, Math.round((event.registered / event.capacity) * 100));
  const isFull = event.registered >= event.capacity;

  return (
    <div data-testid={`event-card-${event.id}`} className="card-lift bg-white rounded-2xl border border-slate-200 shadow-[0_8px_32px_rgba(0,0,0,0.04)] overflow-hidden flex flex-col">
      <div className="relative h-40 overflow-hidden">
        <img src={event.image} alt={event.title} className="w-full h-full object-cover" />
        <div className="absolute top-3 left-3 flex gap-2">
          <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${CATEGORY_STYLES[event.category] || "bg-slate-100 text-slate-700"}`}>
            {event.category}
          </span>
          <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${STATUS_STYLES[event.status] || "bg-slate-100"}`}>
            {isFull ? "Full" : event.status}
          </span>
        </div>
        {event.sample && (
          <span data-testid={`sample-badge-${event.id}`} className="absolute bottom-3 left-3 text-[10px] font-bold px-2.5 py-1 rounded-full bg-slate-900/60 text-slate-200 backdrop-blur-sm border border-white/20">
            Sample Data / Demo Mode
          </span>
        )}
        <span className="absolute top-3 right-3 text-xs font-bold px-2.5 py-1 rounded-full bg-white/90 text-slate-700">
          {event.format}
        </span>
      </div>

      <div className="p-5 flex flex-col flex-1 gap-3">
        <div>
          <h3 className="font-heading font-extrabold text-lg text-slate-900 leading-snug">{event.title}</h3>
          <p className="text-sm text-slate-500 flex items-center gap-1 mt-1">
            {event.organizer}
            {event.verified && <BadgeCheck data-testid={`verified-badge-${event.id}`} className="w-4 h-4 text-emerald-600" />}
          </p>
        </div>

        <div className="space-y-1.5 text-sm text-slate-600">
          <p className="flex items-center gap-2"><MapPin className="w-4 h-4 text-slate-400" />{event.address}, {event.city}</p>
          <p className="flex items-center gap-2"><CalendarDays className="w-4 h-4 text-slate-400" />{event.date}</p>
          <p className="flex items-center gap-2"><Clock className="w-4 h-4 text-slate-400" />{event.time}</p>
        </div>

        <div className="flex flex-wrap gap-1.5">
          {event.skills.map((s) => (
            <span key={s} className="text-xs font-semibold px-2 py-0.5 rounded-full bg-teal-50 text-teal-700 border border-teal-100">{s}</span>
          ))}
          {(event.inclusivity || []).map((x) => (
            <span key={x} className="text-xs font-semibold px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-100 inline-flex items-center gap-1">
              <Accessibility className="w-3 h-3" aria-hidden="true" />{x}
            </span>
          ))}
        </div>

        <div className="mt-auto space-y-3">
          <div>
            <div className="flex justify-between text-xs font-bold text-slate-600 mb-1">
              <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" />{event.registered}/{event.capacity} Relawan Terdaftar</span>
              <span>{pct}%</span>
            </div>
            <Progress value={pct} className="h-2" data-testid={`slot-progress-${event.id}`} />
          </div>

          <div className="flex gap-2">
            <Button
              data-testid={`ikuti-aksi-btn-${event.id}`}
              onClick={() => setRegisterOpen(true)}
              disabled={isFull}
              className="flex-1 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold disabled:bg-slate-200 disabled:text-slate-500"
            >
              {isFull ? "Kuota Penuh" : "Ikuti Aksi"}
            </Button>
            <button
              data-testid={`report-event-btn-${event.id}`}
              onClick={() => setReportOpen(true)}
              title="Laporkan Masalah / Pengaduan"
              className="p-2.5 rounded-full border border-slate-200 text-slate-400 hover:text-rose-600 hover:border-rose-300 transition-colors"
            >
              <Flag className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      <RegisterModal open={registerOpen} onOpenChange={setRegisterOpen} event={event} onRegistered={onRegistered} />
      <ReportModal open={reportOpen} onOpenChange={setReportOpen} targetType="Kegiatan" targetName={event.title} />
    </div>
  );
}
